/**
 * JackpotCounter Component
 *
 * Compact header display showing the current jackpot pool.
 * Features a PokerStars "The Deal" inspired mechanical odometer
 * with individual digit tiles, green glowing digits on dark boxes,
 * and cascading roll animations.
 */

import { memo, useState, useCallback, useEffect } from 'react';
import { useJackpotPool, usePrevious } from '../../hooks/useJackpot';
import { useTranslation } from '../../hooks/useTranslation';
import { publicAsset } from '../../utils/assetUrl';

interface JackpotCounterProps {
  onClick?: () => void;
  className?: string;
}

const DIGIT_HEIGHT = 1.5; // em per digit cell

/**
 * Individual odometer reel for a single digit.
 * Each digit sits inside its own dark tile with a subtle
 * cylindrical gradient, giving the mechanical counter look.
 */
function SlotReel({ digit, delay }: { digit: number; delay: number }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setReady(true));
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <span
      className="inline-block overflow-hidden rounded-[3px]"
      style={{
        height: `${DIGIT_HEIGHT}em`,
        width: '1.05em',
        verticalAlign: 'top',
        background:
          'linear-gradient(180deg, #080808 0%, #181818 40%, #1c1c1c 50%, #181818 60%, #080808 100%)',
        boxShadow:
          'inset 0 2px 4px rgba(0,0,0,0.9), inset 0 -2px 4px rgba(0,0,0,0.9), 0 1px 0 rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span
        className="flex flex-col items-center"
        style={{
          transform: `translateY(-${digit * DIGIT_HEIGHT}em)`,
          ...(ready
            ? {
                transitionProperty: 'transform',
                transitionDuration: '700ms',
                transitionDelay: `${delay}ms`,
                transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.3, 1.02)',
              }
            : {}),
        }}
      >
        {Array.from({ length: 10 }, (_, i) => (
          <span
            key={i}
            className="block text-center font-bold"
            style={{
              height: `${DIGIT_HEIGHT}em`,
              lineHeight: `${DIGIT_HEIGHT}em`,
              color: '#4ade80',
              textShadow: '0 0 8px rgba(74, 222, 128, 0.6), 0 0 2px rgba(74, 222, 128, 0.3)',
            }}
          >
            {i}
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * Mechanical odometer display.
 * Splits a number into individual digit reels inside dark tiles,
 * with comma separators between groups of 3 digits.
 */
function OdometerDisplay({ value, className = '' }: { value: number; className?: string }) {
  const digits = Math.max(0, Math.round(value)).toString().split('').map(Number);
  const totalDigits = digits.length;

  const elements: JSX.Element[] = [];
  digits.forEach((d, i) => {
    const posFromRight = totalDigits - 1 - i;
    const delay = posFromRight * 50;

    elements.push(<SlotReel key={`d${i}`} digit={d} delay={delay} />);

    if (posFromRight > 0 && posFromRight % 3 === 0) {
      elements.push(
        <span
          key={`sep${i}`}
          className="font-bold"
          style={{
            color: 'rgba(74, 222, 128, 0.4)',
            fontSize: '0.75em',
            lineHeight: `${DIGIT_HEIGHT}em`,
            margin: '0 1px',
          }}
        >
          ,
        </span>
      );
    }
  });

  return (
    <span
      className={`inline-flex items-center gap-[2px] ${className}`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {elements}
    </span>
  );
}

export const JackpotCounter = memo(function JackpotCounter({
  onClick,
  className = '',
}: JackpotCounterProps) {
  const { t } = useTranslation();
  const { poolAmount, isLoading } = useJackpotPool();
  const previousAmount = usePrevious(poolAmount);

  const [showTooltip, setShowTooltip] = useState(false);

  const isHighPool = poolAmount !== null && poolAmount > 10_000_000;
  const recentIncrease =
    poolAmount !== null && previousAmount ? Math.max(0, poolAmount - previousAmount) : 0;

  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`flex flex-col items-center px-2 sm:px-4 py-1.5 rounded-xl
                   border border-white/[0.08]
                   hover:border-amber-500/30
                   transition-all duration-300 cursor-pointer group
                   ${isHighPool ? 'animate-golden-shimmer' : ''}
                   ${className}`}
        style={{
          background: 'linear-gradient(180deg, rgba(18,18,28,0.95) 0%, rgba(8,8,12,0.98) 100%)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
        }}
      >
        {/* Title */}
        <span
          className="hidden sm:block text-[9px] uppercase tracking-[0.2em] font-extrabold leading-none mb-1.5"
          style={{
            background: 'linear-gradient(180deg, #fde68a 0%, #f59e0b 50%, #d97706 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.3))',
          }}
        >
          {t('jackpot.label', 'Jackpot')}
        </span>

        {/* Counter row */}
        <div className="flex items-center">
          {/* Token coin */}
          <img
            src={publicAsset('token-coin-32.png')}
            alt=""
            className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-1.5 self-center"
            draggable={false}
          />

          {/* Digit odometer */}
          {isLoading ? (
            <span className="text-sm font-bold" style={{ color: '#4ade80' }}>
              ...
            </span>
          ) : poolAmount === null ? (
            // Unreadable, not empty: a 0 here once told users the jackpot was gone
            // while the server was merely unreachable.
            <span
              className="text-sm font-bold"
              style={{ color: '#4ade80' }}
              title={t('tokens.serviceUnreachable')}
            >
              —
            </span>
          ) : (
            <OdometerDisplay value={poolAmount} className="text-sm" />
          )}
        </div>

        {/* Recent increase indicator */}
        {recentIncrease > 0 && (
          <span className="text-[10px] text-green-400 animate-fade-up mt-0.5 font-semibold">
            +{recentIncrease.toLocaleString()}
          </span>
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50
                        px-3 py-2 rounded-lg
                        bg-secondary-800 border border-secondary-700
                        text-xs text-slate-300 whitespace-nowrap
                        shadow-lg animate-fade-in"
        >
          {t('jackpot.clickToSpin', 'Click to spin the wheel!')}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-secondary-800 border-l border-t border-secondary-700 rotate-45" />
        </div>
      )}

      {/* Glow effect for high pools */}
      {isHighPool && (
        <div className="absolute inset-0 rounded-xl pointer-events-none animate-pulse">
          <div
            className="w-full h-full rounded-xl"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(74,222,128,0.12) 0%, rgba(74,222,128,0) 70%)',
            }}
          />
        </div>
      )}
    </div>
  );
});
